<script setup>
import { computed, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { ScanLine, Sun, Moon, LogOut } from 'lucide-vue-next'
import { useDark, useToggle } from '@vueuse/core'
import { useAuthStore } from '@/stores/authStore'
import PlanBadge from '@/components/billing/PlanBadge.vue'
import UpgradePrompt from '@/components/billing/UpgradePrompt.vue'

const router = useRouter()
const auth = useAuthStore()
const isDark = useDark()
const toggleDark = useToggle(isDark)

const upgradeOpen = ref(false)
const userInitial = computed(() => (auth.user?.email?.[0] || '?').toUpperCase())

async function handleSignOut() {
  await auth.signOut()
  router.push({ name: 'home' })
}
</script>

<template>
  <header class="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
    <div class="container-wide flex h-16 items-center justify-between gap-4">
      <div class="flex items-center gap-3">
        <RouterLink :to="auth.isAuthenticated ? '/dashboard' : '/'" class="flex items-center gap-2">
          <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-white">
            <ScanLine class="h-5 w-5" />
          </span>
          <span class="font-display text-lg font-bold tracking-tight">Papersnap</span>
        </RouterLink>
        <PlanBadge v-if="auth.isAuthenticated" :clickable="true" @click="upgradeOpen = true" />
      </div>

      <nav class="flex items-center gap-2">
        <button
          type="button"
          class="rounded-lg p-2 text-slate-600 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
          @click="toggleDark()"
        >
          <Sun v-if="isDark" class="h-5 w-5" />
          <Moon v-else class="h-5 w-5" />
        </button>

        <template v-if="!auth.isAuthenticated">
          <!-- Blog is a static surface published outside the SPA, so a plain
               anchor (full navigation), not a RouterLink. -->
          <a href="/blog/index.html" class="btn-ghost hidden sm:inline-flex">Blog</a>
          <RouterLink to="/pricing" class="btn-ghost hidden sm:inline-flex">Pricing</RouterLink>
          <RouterLink to="/mcp" class="btn-ghost hidden sm:inline-flex">MCP</RouterLink>
          <RouterLink to="/login" class="btn-ghost">Sign in</RouterLink>
          <RouterLink to="/signup" class="btn-primary">Get started</RouterLink>
        </template>

        <template v-else>
          <div class="hidden items-center gap-2 sm:flex">
            <span class="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
              {{ userInitial }}
            </span>
            <span class="max-w-[160px] truncate text-sm text-slate-600 dark:text-slate-300">
              {{ auth.user?.email }}
            </span>
          </div>
          <button type="button" class="btn-ghost" @click="handleSignOut">
            <LogOut class="h-4 w-4" />
            <span class="hidden sm:inline">Sign out</span>
          </button>
        </template>
      </nav>
    </div>

    <UpgradePrompt v-model="upgradeOpen" />
  </header>
</template>
